import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { DataService } from '@app-core/services/data/data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AsyncReportType, DEFAULT_THEME } from '@app-core/constants/constants';
import { DateService } from '@app-core/services/date/date.service';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { DriverCoachingEffectivenessResponse, StatsData } from '@app-reports/models/reports.model';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { RequestDownloadReportBody } from '@app-core/models/core.model';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';

interface CoachingEffectivenessDriverDownloadBody extends RequestDownloadReportBody {
  unit?: string;
  language?: string;
  driverId: string;
  coachingSessionId: number;
}

@Component({
  selector: 'app-coaching-effectiveness-driver',
  templateUrl: './coaching-effectiveness-driver.component.html',
  styleUrls: ['./coaching-effectiveness-driver.component.scss'],
})
export class CoachingEffectivenessDriverComponent implements OnInit {
  public driverId = new FormControl('');

  public getCoachingEffectivenessDetailsLoader: boolean;
  public enableDownloadReport: boolean;
  public downloadReportLoader: boolean;
  public sessionListLoader: boolean;
  public showError: boolean;
  public showDetails: boolean;
  public csvDownloadSuccess: boolean;

  public coachingSessionList = [];
  public driverCoachingEffectiveness: DriverCoachingEffectivenessResponse;
  public statsData: StatsData;
  public selectedCoachingIndex: number = -1;
  public isEarlyAccess: boolean;

  public currentMetricUnit: string = null;
  public currentTheme: string;
  public currentDateFormat: string;
  public currentTimeZone: string;
  public currentLanguage: string;

  private fleetId: string;
  private coachingSessionId: number = 1;
  private noOfDays: number = 180;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public dataService: DataService,
    private location: Location,
    private dateService: DateService,
    private reportsService: ReportsService,
    private asyncDownloadsService: AsyncDownloadsService,
    private snackbarService: SnackBarService,
    public translate: TranslateService
  ) {}

  public ngOnInit(): void {
    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        if (this.driverId.value && this.selectedCoachingIndex !== -1) {
          this.getCoachingEffectivenessDetails();
        }
      }
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
    });

    this.dataService._currentDateFormat.subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });

    this.dataService._currentTheme.subscribe((value: string) => {
      this.currentTheme = value || DEFAULT_THEME;
    });

    this.dataService._currentTimeZone.subscribe((value: string) => {
      this.currentTimeZone = value;
    });

    this.dataService._currentLanguage.subscribe((value: string) => {
      this.currentLanguage = value;
    });
  }

  public navigateBack() {
    this.location.back();
  }

  public driverChanged(driverId: string = '') {
    this.driverId.patchValue(driverId);
    this.enableDownloadReport = false;
    if (driverId) {
      this.getListOfCoachingSession();
    }
  }

  private getListOfCoachingSession() {
    if (!this.driverId.value) {
      return;
    }

    const dateRange = this.dateService.getDateRangeInISO(this.noOfDays);
    const { from, to } = dateRange;

    this.sessionListLoader = true;
    const params = {
      skip: 0,
      limit: 500,
      startDate: from,
      endDate: to,
    };

    params['driverIds[]'] = this.driverId.value;

    this.reportsService
      .getCoachingSessionList(params)
      .pipe(
        finalize(() => {
          this.sessionListLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          if (res) {
            const previouslist = this.coachingSessionList;
            const isSameList =
              previouslist.length == res?.rows.length &&
              previouslist.every(function (element, index) {
                return element === res.rows[index];
              });
            if (isSameList) {
              this.enableDownloadReport = true;
            } else {
              this.enableDownloadReport = false;
            }
            const { rows = [] } = res || {};
            this.coachingSessionList = rows;
          }
        },
        () => {
          this.coachingSessionList = [];
        }
      );
  }

  public coachingSessionChange(event) {
    this.coachingSessionId = event?.value || 0;
    this.enableDownloadReport = false;
    this.selectedCoachingIndex = this.coachingSessionList.findIndex((item) => item.coachingSessionId === this.coachingSessionId);
    this.getCoachingEffectivenessDetails();
  }

  public getCoachingEffectivenessDetails() {
    this.enableDownloadReport = true;
    this.showDetails = true;
    this.showError = false;
    this.isEarlyAccess = false;
    this.getCoachingEffectivenessDetailsLoader = true;
    const params = {
      fleetId: this.fleetId,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
    };
    this.reportsService
      .getCoachingEffectivenessDriverDetails(this.coachingSessionId, this.driverId.value, params)
      .pipe(
        finalize(() => {
          this.getCoachingEffectivenessDetailsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: DriverCoachingEffectivenessResponse) => {
          this.driverCoachingEffectiveness = res;

          this.statsData = {
            driverId: this.driverCoachingEffectiveness.driverId,
            coachingSessionId: this.driverCoachingEffectiveness.coachingSessionId,
            driverName: this.driverCoachingEffectiveness.driverName,
            coachName: this.driverCoachingEffectiveness.coachName,
            sessionDuration: this.driverCoachingEffectiveness.sessionDuration,
            totalDistance: this.driverCoachingEffectiveness.totalDistance,
            totalViolations: this.driverCoachingEffectiveness.totalViolations,
            mostFrequentEventType: this.driverCoachingEffectiveness.mostFrequentEventType,
            leastFrequentEventType: this.driverCoachingEffectiveness.leastFrequentEventType,
            scoreDiff: this.driverCoachingEffectiveness.scoreDiff,
            score: this.driverCoachingEffectiveness.score,
            originalLeastFrequentEventType: this.driverCoachingEffectiveness.originalLeastFrequentEventType,
            originalMostFrequentEventType: this.driverCoachingEffectiveness.originalMostFrequentEventType,
          };
        },
        (error) => {
          if (error?.status === 409) {
            this.isEarlyAccess = true;
            this.showDetails = false;
          } else {
            this.showError = true;
            this.showDetails = false;
          }
        }
      );
  }

  public downloadReport() {
    this.csvDownloadSuccess = null;
    this.downloadReportLoader = true;
    const body: CoachingEffectivenessDriverDownloadBody = {
      reportType: 'DRIVER_COACHING_EFFECTIVENESS',
      reportFormat: 'PDF',
      fleetId: this.fleetId,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      language: this.currentLanguage,
      driverId: this.driverId.value,
      coachingSessionId: this.coachingSessionId,
    };
    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => {
          this.downloadReportLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.driverCoachingEffectiveness.key);
  }
}
